import type { Language } from '@eformulieren/engine'
import type { ProjectionSection } from '@eformulieren/model'
import { type ReactNode, useId, useState } from 'react'
import { CircleIcon } from '../icons/CircleIcon.js'
import { PencilIcon } from '../icons/PencilIcon.js'
import { CheckIcon } from '../icons/CheckIcon.js'
import { CursorIcon } from '../icons/CursorIcon.js'
import { SlotIcon } from '../icons/SlotIcon.js'
import { inLanguage } from '../language/text.js'
import { roleWord } from './shareModel.js'

/**
 * De meelopende inhoudsopgave (R-02, R-142).
 *
 * **Gebouwd uit de projectie en niet uit een DOM-scan.** De wireframe-component
 * doet het laatste; dan staat er een sectie in de lijst die deze persoon niet mag
 * zien, of ontbreekt er een die nog niet is getekend. Uit de projectie klopt de
 * lijst per constructie met wat deze persoon mag zien (R-68, ontwerp 6.1).
 *
 * **Knoppen en geen ankerlinks.** Een link naar een ingeklapte sectie doet niets;
 * kiezen moet de sectie openklappen, ernaartoe scrollen en de focus meenemen. Dat
 * is gedrag en dus een knop. Knoppen zitten vanzelf in de tabvolgorde, waarmee
 * R-142 zonder eigen toetsafhandeling is gehaald.
 *
 * Hij verschijnt vanaf twee secties (ontwerp 6.1): bij één sectie is een
 * inhoudsopgave ruis.
 *
 * **Neutraal tot de invuller zelf controleert** (ontwerp van het invulscherm, 29 oktober 2026).
 * Per sectie staat een teller "5/8": hoeveel verplichte velden er zijn ingevuld van het totaal, en
 * een vinkje zodra er in de sectie geen enkele harde controle meer openstaat. Geen rode driehoek en
 * geen "N harde controles open" meer: een leeg veld in een concept dat weken loopt is geen fout. Wat
 * R-24 vraagt (de invuller ziet tijdens het invullen wat nog niet voldoet) blijft staan in de teller,
 * in de melding bij het veld na het verlaten ervan (R-25) en in het overzicht achter "Controleren en
 * indienen". Het vinkje hangt aan de harde controles en niet aan de teller: een sectie waarvan alles
 * is ingevuld maar een woordlimiet overschrijdt, krijgt geen vinkje.
 *
 * **Op een smal formulier een knop en geen lijst** ("Sectie 2 van 9 · Overzicht"). De lijst staat
 * dan achter die knop, zodat de invuller op een telefoon niet eerst langs dertien regels scrolt; de
 * keuze voor een sectie klapt hem weer in. Welke van de twee geldt bepaalt de containerquery in
 * `schil.css`, niet dit component.
 *
 * **Vaste kolommen per regel** (live test van 3 november 2026). Status, nummer, titel en rechts de
 * teller staan elk in een eigen kolom van één raster over de hele lijst, zodat de nummers en titels
 * onder elkaar staan, ook bij een sectie zonder statusicoon, en een lange titel afbreekt in plaats van
 * onder de teller door te lopen. Wat onder de titel hoort (opmerkingen, het recht) staat in de
 * titelkolom op een eigen regel.
 *
 * **Het recht één keer, niet per regel** (zelfde dag). Wie via een deling binnenkwam las "schrijven"
 * achter elke sectie. Nu zegt de kop één keer wat hij mag ("Gedeeld met u: u kunt meeschrijven"), en
 * alleen een sectie die daarvan afwijkt krijgt een label met icoon en tekst. In de naam van de knop
 * staat het recht wel bij elke sectie, zodat een schermlezer het per sectie hoort (R-139).
 */

export interface TableOfContentsProps {
  readonly secties: readonly ProjectionSection[]
  readonly taal: Language
  readonly active: string | undefined
  readonly onKies: (sectionId: string) => void
  /** Het label van de navigatie, in de taal van de invuller. */
  readonly label: string
  /**
   * Hoeveel opmerkingdraadjes er per sectie nog openstaan (R-73), of niets.
   *
   * Hier staat het als getal en niet voluit, want de lijst moet smal blijven; het woord staat
   * ernaast voor de schermlezer. Zie de kop van `openstaandeOpmerkingen` in `SectionPanel` voor
   * waarom dit getal bestaat.
   */
  readonly outstandingPer?: (sectionId: string) => number
  /**
   * Hoeveel harde controles er per sectie nog openstaan (`evaluate()`'s
   * `status.sectie[id].openstaandeHardeControles`), of niets.
   *
   * Zelfde patroon als `outstandingPer`: een functie en geen record, en optioneel omdat een
   * aanroeper zonder `evaluate`-uitkomst (een preview zonder stand, een test) niets hoeft te
   * berekenen. Ontbreekt hij, dan blijft de inhoudsopgave zoals hij vóór deze opdracht was: geen
   * statusicoon, geen voortgangsbalk, geen samenvattende alert (c1, c2, c4 — visuele
   * verrijking, geen eis, R-157).
   *
   * **Niets voor een sectie waarin niets te doen is** (audit van 30 oktober 2026). Een sectie met
   * alleen overgenomen waarden, of zonder één verplicht veld of criterium, kreeg het vinkje
   * "compleet" omdat er niets openstond, en een leeg formulier zei zo "3 van 12 secties
   * compleet". Zo'n sectie krijgt geen statusicoon en telt niet mee in de voortgang. Of er iets te
   * doen is, rekent de aanroeper uit; dit component leidt geen regel af. De aanroeper telt hier
   * ook een criterium zonder oordeel mee, want ook dat houdt de beurt tegen.
   */
  readonly hardChecksPer?: (sectionId: string) => number | undefined
  /**
   * Hoeveel verplichte velden er per sectie zijn ingevuld, en van hoeveel (de teller "5/8"), of
   * niets voor een sectie zonder verplichte velden of buiten de eigen laag.
   *
   * Uit de engine (`requiredFields`, `isEmpty`) en de zichtbaarheid, uitgerekend door de aanroeper:
   * dit component leidt geen regel af.
   */
  readonly progressPer?: (sectionId: string) => SectionProgress | undefined
  /** In de toetsgedaante: de stand van de criteria per sectie, in plaats van de teller. */
  readonly criteriaPer?: (sectionId: string) => CriteriaState | undefined
  /** In de toetsgedaante: hoeveel criteria een oordeel hebben, voor de kop van de lijst. */
  readonly criteriaProgress?: { readonly beoordeeld: number; readonly totaal: number }
  /**
   * Of deze sectie aanpassing vraagt na een teruglegging (R-132, R-98), of niets.
   *
   * Bij aanvullen staat de sectie die NWO terugstuurde zo ook in de lijst gemarkeerd, en niet
   * alleen in de band bovenaan en bij de sectie zelf: wie door een lang formulier navigeert
   * (R-02, R-142) ziet waar hij heen moet. Met een icoon en een tekst voor de schermlezer, niet
   * alleen met kleur (R-139).
   */
  readonly adjustmentPer?: (sectionId: string) => boolean
  /**
   * De echte `<nav>`-DOM-node, voor `useVolgSticky` in `Formulier.tsx`.
   *
   * Een callback-ref en geen object-ref: dit component is een gewone functie en
   * geen `forwardRef`, en dit past bij het bestaande `meldAan`-patroon in
   * `Formulier.tsx` voor precies dezelfde reden (de aanroeper moet de knoop van dit
   * component los kunnen volgen, ook al mount hij pas vanaf `MINIMUM_SECTIONS`).
   */
  readonly navRef?: (element: HTMLElement | null) => void
  /**
   * Voor wie via een deling binnenkwam: bovenaan één zin met wat hij mag ("Gedeeld met u: u kunt
   * meeschrijven"), een label alleen bij een sectie die daarvan afwijkt, en het recht per sectie in de
   * naam van de knop (audit delen G-16, mockup 07, live test van 3 november 2026). Zonder deze prop niets.
   */
  readonly rechtZichtbaar?: boolean
  /** Of de secties een nummer dragen: volgt de nummering van de definitie (R-04), standaard aan. */
  readonly genummerd?: boolean
}

/**
 * De stand van de criteria die over een sectie gaan, voor de inhoudsopgave van de toetser (ontwerp
 * van het toetsscherm, R-98): geen criterium, nog een zonder oordeel, of alle beoordeeld en dan of
 * ze voldoen.
 */
export type CriteriaState = 'geen' | 'open' | 'voldoet' | 'voldoet-niet'

/** Het recht per sectie in de naam van de knop, en zichtbaar bij een sectie die van de kop afwijkt. */
const READ = { nl: 'alleen lezen', en: 'read only' }
const WRITE = { nl: 'schrijven', en: 'write' }

/**
 * De kop boven de lijst voor wie via een deling binnenkwam: wat hij mag, in de woorden van de
 * delendialoog (`roleWord`: Meelezen, Meeschrijven). "Gedeeld met u" zegt ook dat alleen de gedeelde
 * secties in de lijst staan. Bij gemengde rechten volgt de kop wat de meeste secties zijn, bij gelijke
 * stand meeschrijven, en wijst hij naar het label bij de andere.
 */
function sharedHeader(majority: 'bewerken' | 'lezen', mixed: boolean, language: Language): string {
  const role = roleWord(majority === 'bewerken' ? 'meeschrijven' : 'meelezen', language).toLowerCase()
  const first = language === 'nl' ? `Gedeeld met u: u kunt ${role}` : `Shared with you: you can ${role}`
  if (!mixed) return first
  if (majority === 'bewerken') return language === 'nl' ? `${first}. Een paar secties kunt u alleen lezen.` : `${first}. A few sections are read only.`
  return language === 'nl' ? `${first}. In een paar secties kunt u ook schrijven.` : `${first}. In a few sections you can also write.`
}

/** Alleen voor de schermlezer: waarom er een nummer kan ontbreken (G-17). */
const NUMBERS_HINT = {
  nl: 'De nummers zijn die van het hele formulier.',
  en: 'The numbers are those of the whole form.',
}

const CRITERIA_STATE: Record<CriteriaState, { nl: string; en: string }> = {
  geen: { nl: 'geen criterium', en: 'no criterion' },
  open: { nl: 'open', en: 'open' },
  voldoet: { nl: 'voldoet', en: 'meets' },
  'voldoet-niet': { nl: 'voldoet niet', en: 'does not meet' },
}

/** Ingevulde verplichte velden van een sectie, en het totaal. */
export interface SectionProgress {
  readonly ingevuld: number
  readonly totaal: number
}

export const MINIMUM_SECTIONS = 2

/** Het woord bij het getal, alleen voor de schermlezer (R-139, R-143). */
const COUNTER_WORD = (count: number): { nl: string; en: string } => ({
  nl: `${String(count)} ${count === 1 ? 'opmerking' : 'opmerkingen'}`,
  en: `${String(count)} ${count === 1 ? 'comment' : 'comments'}`,
})

/** De markering van een sectie die aanpassing vraagt; zichtbaar is alleen het icoon. */
const ADJUSTMENT = { nl: 'vraagt aanpassing', en: 'needs changes' }

/** De teller voor de schermlezer: "5 van 8 verplichte velden ingevuld". */
const PROGRESS_LABEL = (progress: SectionProgress): { nl: string; en: string } => ({
  nl: `${String(progress.ingevuld)} van ${String(progress.totaal)} verplichte velden ingevuld`,
  en: `${String(progress.ingevuld)} of ${String(progress.totaal)} required fields filled in`,
})

/** De markering van een sectie zonder openstaande harde controle, voor de schermlezer. */
const COMPLETE = { nl: 'ingevuld', en: 'filled in' }

/**
 * Hoeveel secties klaar zijn van de secties waarin iets te doen is, of niets als er nergens iets te doen
 * is (nalezen, een lezer zonder schrijfrecht): "0 van 0 secties ingevuld" zegt niets. Ook de voortgang
 * in de kop telt hiermee (besluit van 2 november 2026), zodat kop en inhoudsopgave hetzelfde zeggen.
 */
export function completeSections(
  sections: readonly ProjectionSection[],
  openPer: (sectionId: string) => number | undefined,
): { readonly klaar: number; readonly totaal: number } | undefined {
  const countable = sections.filter((section) => openPer(section.id) !== undefined)
  if (countable.length === 0) return undefined
  return { klaar: countable.filter((section) => openPer(section.id) === 0).length, totaal: countable.length }
}

/** Voortgang boven de lijst, alleen als `hardeControlesPer` iets oplevert (c1). */
export const PROGRESS = (complete: number, total: number): { nl: string; en: string } => ({
  nl: `${String(complete)} van ${String(total)} secties ingevuld`,
  en: `${String(complete)} of ${String(total)} sections complete`,
})

/** De kop boven de lijst, zoals het ontwerp hem noemt. */
const HEADER = { nl: 'Inhoud', en: 'Contents' }

/** De kop in de toetsgedaante (ontwerp van het toetsscherm). */
const CRITERIA_HEADER = { nl: 'Criteria', en: 'Criteria' }

const CRITERIA_PROGRESS = (assessed: number, total: number): { nl: string; en: string } => ({
  nl: `${String(assessed)} van ${String(total)} beoordeeld`,
  en: `${String(assessed)} of ${String(total)} assessed`,
})

/** De knop op een smal formulier: waar je bent, en dat de lijst erachter staat. */
/**
 * "Sectie 2 van 3", en als het nummer uit de definitie anders is (een medeschrijver met een deel van de
 * secties, G-17): "Sectie 3, 2 van 3", zodat het nummer hetzelfde is als bij de aanvrager.
 */
const POSITION = (place: number, total: number, number?: number): { nl: string; en: string } =>
  number === undefined || number === place
    ? { nl: `Sectie ${String(place)} van ${String(total)}`, en: `Section ${String(place)} of ${String(total)}` }
    : { nl: `Sectie ${String(number)}, ${String(place)} van ${String(total)}`, en: `Section ${String(number)}, ${String(place)} of ${String(total)}` }

export function TableOfContents({
  secties: sections,
  taal: language,
  active,
  onKies: onChoose,
  label,
  outstandingPer,
  hardChecksPer,
  progressPer,
  criteriaPer,
  criteriaProgress,
  adjustmentPer,
  navRef,
  rechtZichtbaar: showRight = false,
  genummerd: numbered = true,
}: TableOfContentsProps): ReactNode {
  // Op een smal formulier staat de lijst achter een knop; daar is hij standaard dicht. Op een breed
  // formulier negeert de stylesheet deze stand en staat de lijst er altijd.
  const [open, setOpen] = useState(false)
  const listId = useId()
  if (sections.length < MINIMUM_SECTIONS) return null
  const activePlace = Math.max(0, sections.findIndex((section) => section.id === active))
  const activeSection = sections[activePlace]
  const count = hardChecksPer === undefined ? undefined : completeSections(sections, hardChecksPer)
  const completeCount = count?.klaar
  // Het recht dat de meeste gedeelde secties hebben; een bronsectie telt niet, die leest iedereen.
  const rightSections = showRight ? sections.filter((section) => section.type !== 'bron') : []
  const writing = rightSections.filter((section) => section.recht === 'bewerken').length
  const majority: 'bewerken' | 'lezen' = writing * 2 >= rightSections.length ? 'bewerken' : 'lezen'
  const mixed = writing > 0 && writing < rightSections.length
  return (
    <nav className="ef-inhoudsopgave" aria-label={label} ref={navRef} data-open={open ? 'true' : 'false'}>
      <button
        type="button"
        className="ef-inhoudsopgave__wissel"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((previous) => !previous)}
      >
        <span>
          <span className="ef-inhoudsopgave__wisselplek">
            {inLanguage(POSITION(activePlace + 1, sections.length, activeSection?.nummer), language)} &middot;{' '}
          </span>
          <span className="ef-inhoudsopgave__wisseltitel">
            {activeSection === undefined ? null : inLanguage(activeSection.titel, language)}
          </span>
        </span>
        <span className="ef-inhoudsopgave__wisselpijl" aria-hidden="true" />
      </button>
      <div className="ef-inhoudsopgave__inhoud" id={listId}>
      <p className="ef-inhoudsopgave__kop">{inLanguage(criteriaProgress === undefined ? HEADER : CRITERIA_HEADER, language)}</p>
      {!showRight || rightSections.length === 0 ? null : (
        <p className="ef-inhoudsopgave__gedeeld">
          {sharedHeader(majority, mixed, language)}
          {numbered ? <span className="ef-sr-only"> {inLanguage(NUMBERS_HINT, language)}</span> : null}
        </p>
      )}
      {criteriaProgress === undefined ? null : (
        <p className="ef-inhoudsopgave__voortgang">
          <span>{inLanguage(CRITERIA_PROGRESS(criteriaProgress.beoordeeld, criteriaProgress.totaal), language)}</span>
        </p>
      )}
      {/* De zin en geen balk ernaast: het ontwerp van het invulscherm toont er geen, en de balk zei
          niets wat de zin niet al zei (audit R-13). */}
      {completeCount === undefined || criteriaProgress !== undefined ? null : (
        <p className="ef-inhoudsopgave__voortgang">
          <span>{inLanguage(PROGRESS(completeCount, count?.totaal ?? 0), language)}</span>
        </p>
      )}
      <ol className="ef-inhoudsopgave__lijst">
        {sections.map((section, place) => {
          const outstanding = outstandingPer?.(section.id) ?? 0
          const hardChecks = hardChecksPer?.(section.id)
          const progress = criteriaPer === undefined ? progressPer?.(section.id) : undefined
          // "geen criterium" niet bij elke sectie: in de toetsgedaante staat er alleen een stand bij
          // een sectie waar een criterium over gaat (ontwerp van het toetsscherm, R-98). De vinkjes
          // en nummers van de invulkant staan er dan ook niet: of de aanvrager een sectie compleet
          // had, is voor de toetser geen stand van zijn eigen werk.
          const criteria = criteriaPer?.(section.id)
          const reviewing = criteriaPer !== undefined
          const isActive = section.id === active
          const adjustment = adjustmentPer?.(section.id) === true
          const showNumber = !reviewing && numbered
          const showProgress = progress !== undefined && progress.totaal > 0
          const right = showRight && section.type !== 'bron' ? section.recht : undefined
          const rightDiffers = right !== undefined && mixed && right !== majority
          // De naam van de knop als zin: nummer en titel, dan de rest met komma's ertussen ("2 Overzicht,
          // 0 van 6 verplichte velden ingevuld, 2 opmerkingen"). Een verborgen komma per deel, zoals
          // eerst, las Chromium als "Overzicht , 0 van 6" (besluit van 1 november 2026 over tellingen).
          const name = [
            `${showNumber ? `${String(section.nummer ?? place + 1)} ` : ''}${inLanguage(section.titel, language)}`,
            hardChecks === 0 && !reviewing ? inLanguage(COMPLETE, language) : undefined,
            adjustment ? inLanguage(ADJUSTMENT, language) : undefined,
            showProgress ? inLanguage(PROGRESS_LABEL(progress), language) : undefined,
            right === undefined ? undefined : inLanguage(right === 'bewerken' ? WRITE : READ, language),
            criteria === undefined || criteria === 'geen' ? undefined : inLanguage(CRITERIA_STATE[criteria], language),
            outstanding === 0 ? undefined : inLanguage(COUNTER_WORD(outstanding), language),
          ]
            .filter((part): part is string => part !== undefined)
            .join(', ')
          return (
            <li key={section.id}>
              <button
                type="button"
                className="ef-inhoudsopgave__item"
                // aria-current en geen eigen klasse alleen: de markering moet ook
                // voor een schermlezer bestaan en niet alleen als kleur (R-139).
                aria-current={isActive ? 'true' : undefined}
                aria-label={name}
                data-aanpassing={adjustment ? 'true' : undefined}
                data-compleet={hardChecks === 0 && !reviewing ? 'true' : undefined}
                onClick={() => {
                  setOpen(false)
                  onChoose(section.id)
                }}
              >
                {/* De eigen stand, ook bij de actieve sectie: die zie je aan aria-current en de
                    linkerstreep. Het potlood staat alleen nog bij "aanpassing gevraagd", zodat één
                    icoon één betekenis heeft (audit R-13). */}
                {hardChecks === undefined || reviewing ? null : hardChecks === 0 ? (
                  <CheckIcon aria-hidden="true" className="ef-inhoudsopgave__status" data-stand="compleet" />
                ) : (
                  <CircleIcon aria-hidden="true" className="ef-inhoudsopgave__status" data-stand="open" />
                )}
                {/* Geen icoon voor een sectie waarin niets te doen is: de plek blijft leeg, want de
                    kolom staat in het raster van de lijst en niet in deze regel. */}
                {/* Het nummer uit de definitie, zodat "sectie 3" voor iedereen dezelfde is (G-17), en alleen
                    als de definitie nummert (R-04). */}
                {!showNumber ? null : <span className="ef-inhoudsopgave__nummer">{section.nummer ?? place + 1}</span>}
                <span className="ef-inhoudsopgave__naam">
                  {inLanguage(section.titel, language)}
                  {outstanding === 0 ? null : (
                    // Het getal met het woord erbij en niet een kaal cijfer naast "3/5" (audit van 30
                    // oktober 2026): zonder woord was niet te zien dat het om opmerkingen ging. Onder de
                    // titel, zodat de teller ernaast de titel niet wegdrukt (1 november 2026).
                    <span className="ef-inhoudsopgave__opmerkingen">{inLanguage(COUNTER_WORD(outstanding), language)}</span>
                  )}
                  {!rightDiffers ? null : (
                    // Alleen waar het recht afwijkt van de kop, met icoon en tekst en niet alleen kleur
                    // (R-139); dezelfde woorden als in de naam van de knop (WCAG 2.5.3).
                    <span className="ef-inhoudsopgave__recht" data-recht={right}>
                      {right === 'bewerken' ? <CursorIcon aria-hidden="true" /> : <SlotIcon aria-hidden="true" />}
                      {inLanguage(right === 'bewerken' ? WRITE : READ, language)}
                    </span>
                  )}
                </span>
                {/* De rechterkolom: wat er bij deze sectie te tellen of te melden is, in één cel, zodat de
                    titelkolom voor elke regel even breed is. */}
                {!adjustment && !showProgress && (criteria === undefined || criteria === 'geen') ? null : (
                  <span className="ef-inhoudsopgave__eind">
                    {adjustment ? (
                      <span className="ef-inhoudsopgave__aanpassing">
                        <PencilIcon aria-hidden="true" />
                      </span>
                    ) : null}
                    {!showProgress ? null : (
                      <span className="ef-inhoudsopgave__teller">
                        {progress.ingevuld}/{progress.totaal}
                      </span>
                    )}
                    {criteria === undefined || criteria === 'geen' ? null : (
                      <span className="ef-inhoudsopgave__criteria" data-stand={criteria}>
                        {inLanguage(CRITERIA_STATE[criteria], language)}
                      </span>
                    )}
                  </span>
                )}
              </button>
            </li>
          )
        })}
      </ol>
      </div>
    </nav>
  )
}
